import { Inject, Injectable, OnDestroy } from '@angular/core';
import FlexSearch, { EnrichedDocumentSearchResultSetUnit } from 'flexsearch';
import { Subject, Subscription } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { BatchUnit } from './create-index';
import { FLEXSEARCH_WORKER } from './flexsearch-worker.token';
import {
  FlexsearchWorkerMessage,
  FlexsearchWorkerResponseMessage,
} from './flexsearch-worker.types';
import { SearchResultDocument } from './search-types';

export interface BuildFlexsearchParams {
  key: string;
  prefix: string | null;
  language: string | null;
  flexsearchOptions: FlexSearch.IndexOptions<SearchResultDocument, any>;
  additionalMetadata: Partial<SearchResultDocument>;
  units: BatchUnit[];
}

export interface RestoreFlexsearchParams {
  key: string;
  prefix: string;
  language: string | null;
  flexsearchOptions: FlexSearch.IndexOptions<SearchResultDocument, any>;
}

/**
 * Low-level bridge to the FlexSearch Web Worker: turns the fire-and-forget
 * `postMessage` channel into promise-returning calls by correlating each
 * reply to its request `requestID` (same pattern as `HeliaWorkerService`).
 *
 * The minimal worker plumbing is inlined here (rather than extending the
 * `WorkerService` base in `@document-vault/angular-iirds-search`) for the
 * same bundling reason as the IPFS worker service: that library's barrel
 * eagerly re-exports the `@xenova/transformers`-backed embedding worker,
 * which must not be dragged into the eager root bundle.
 *
 * Lifecycle (feature-flag gate) lives in `provideFlexsearchWorker`; this
 * service assumes the worker (if any) is ready to receive messages.
 */
@Injectable()
export class FlexsearchWorkerService implements OnDestroy {
  private readonly onMessage$ = new Subject<MessageEvent<FlexsearchWorkerMessage>>();

  constructor(@Inject(FLEXSEARCH_WORKER) private readonly worker: Worker | null) {
    if (this.worker) {
      this.worker.onmessage = (event) => this.onMessage$.next(event);
    } else {
      console.warn('FlexSearch worker is not available in this environment.');
    }
  }

  ngOnDestroy(): void {
    this.worker?.terminate();
    this.onMessage$.complete();
  }

  /** Whether the worker exists (Web Workers supported and FlexSearch enabled). */
  get available(): boolean {
    return !!this.worker;
  }

  /**
   * Builds the index for `key` from a fully-gathered batch, persisting it
   * when `prefix` is set. `onProgress` fires as the worker works through the
   * batch. Resolves with the number of documents added.
   */
  build(params: BuildFlexsearchParams, onProgress?: (counter: number, total: number) => void): Promise<number> {
    const requestID = uuid();
    return this.request<number>(
      requestID,
      { command: 'build', payload: { requestID, ...params } },
      (message, resolve, reject) => {
        if (message.command === 'buildProgress') {
          onProgress?.(message.payload.counter, message.payload.total);
          return false;
        }
        if (message.command === 'buildDone') {
          resolve(message.payload.entryCount);
          return true;
        }
        if (message.command === 'error') {
          reject(new Error(message.payload.message));
          return true;
        }
        return false;
      }
    );
  }

  /** Queries the in-memory index for `key`. */
  search(
    key: string,
    query: string,
    limit: number
  ): Promise<EnrichedDocumentSearchResultSetUnit<SearchResultDocument>[]> {
    const requestID = uuid();
    return this.request<EnrichedDocumentSearchResultSetUnit<SearchResultDocument>[]>(
      requestID,
      { command: 'search', payload: { requestID, key, query, limit } },
      (message, resolve, reject) => {
        if (message.command === 'searchResult') {
          resolve(message.payload.results);
          return true;
        }
        if (message.command === 'error') {
          reject(new Error(message.payload.message));
          return true;
        }
        return false;
      }
    );
  }

  /** Restores a persisted index into memory under `key`. Resolves `false` if none exists. */
  restore(params: RestoreFlexsearchParams): Promise<boolean> {
    const requestID = uuid();
    return this.request<boolean>(
      requestID,
      { command: 'restore', payload: { requestID, ...params } },
      (message, resolve, reject) => {
        if (message.command === 'restoreDone') {
          resolve(message.payload.ok);
          return true;
        }
        if (message.command === 'error') {
          reject(new Error(message.payload.message));
          return true;
        }
        return false;
      }
    );
  }

  hasStoredIndex(prefix: string, language: string | null): Promise<boolean> {
    const requestID = uuid();
    return this.request<boolean>(
      requestID,
      { command: 'hasStoredIndex', payload: { requestID, prefix, language } },
      (message, resolve, reject) => {
        if (message.command === 'hasStoredIndexResult') {
          resolve(message.payload.exists);
          return true;
        }
        if (message.command === 'error') {
          reject(new Error(message.payload.message));
          return true;
        }
        return false;
      }
    );
  }

  /** Discards the in-memory index for `key` and its persisted copy, if any. */
  remove(key: string, prefix: string, language?: string | null): Promise<void> {
    const requestID = uuid();
    return this.request<void>(
      requestID,
      { command: 'remove', payload: { requestID, key, prefix, language } },
      (message, resolve, reject) => {
        if (message.command === 'removeDone') {
          resolve();
          return true;
        }
        if (message.command === 'error') {
          reject(new Error(message.payload.message));
          return true;
        }
        return false;
      }
    );
  }

  /** Hosted-mode cleanup: keep only the persisted index for `prefix`. */
  keepOnly(prefix: string): Promise<void> {
    const requestID = uuid();
    return this.request<void>(
      requestID,
      { command: 'keepOnly', payload: { requestID, prefix } },
      (message, resolve, reject) => {
        if (message.command === 'cleanupDone') {
          resolve();
          return true;
        }
        if (message.command === 'error') {
          reject(new Error(message.payload.message));
          return true;
        }
        return false;
      }
    );
  }

  /** Upload-mode cleanup: keep only persisted indexes whose prefix is valid. */
  pruneExcept(validPrefixes: string[]): Promise<void> {
    const requestID = uuid();
    return this.request<void>(
      requestID,
      { command: 'pruneExcept', payload: { requestID, validPrefixes } },
      (message, resolve, reject) => {
        if (message.command === 'cleanupDone') {
          resolve();
          return true;
        }
        if (message.command === 'error') {
          reject(new Error(message.payload.message));
          return true;
        }
        return false;
      }
    );
  }

  private request<T>(
    requestID: string,
    outbound: FlexsearchWorkerMessage,
    onMessage: (
      message: FlexsearchWorkerResponseMessage,
      resolve: (value: T) => void,
      reject: (error: unknown) => void
    ) => boolean
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (!this.worker) {
        reject(new Error('FlexSearch worker is not available'));
        return;
      }

      let subscription: Subscription | undefined;
      const cleanup = () => subscription?.unsubscribe();

      subscription = this.onMessage$.subscribe((event) => {
        const data = event.data as FlexsearchWorkerResponseMessage;
        if (!data || !('payload' in data) || data.payload.requestID !== requestID) {
          return;
        }
        const done = onMessage(
          data,
          (value) => {
            cleanup();
            resolve(value);
          },
          (error) => {
            cleanup();
            reject(error);
          }
        );
        if (done) {
          cleanup();
        }
      });

      this.worker.postMessage(outbound);
    });
  }
}
